import { useEffect, useRef } from "react";

import type { PremioPublic, SoundStyle } from "@juegossc/shared";
import { playEvent } from "../../lib/audio";
import { getCanvasFont } from "../../lib/fonts";

// Modelo física (configurable por sucursal desde admin):
// - velocidadMin: velocidad piso (rad/frame). Mayor = gira más rápido.
// - variacion: velocidad extra aleatoria sumada al mínimo cada giro.
// - frenado: factor multiplicativo por frame. 0.985 = pierde 1.5%/frame.
// - umbralCasiPasa: si está casi-en-target al frenarse, animación
//   dramática (por ahora no aplicado, hook reservado).
//
// Modelo iterativo: cada frame velocidad *= frenado, rotación +=
// velocidad. Cortamos cuando velocidad cae bajo UMBRAL_STOP.
// Computamos v0 inverso para garantizar que la rotación final caiga
// EXACTO en el segmento target del server (precisión perfecta sin
// importar variación aleatoria).

type PhysicsParams = {
  velocidadMin: number;
  variacion: number;
  frenado: number;
  umbralCasiPasa: number;
};

type Props = {
  premios: PremioPublic[];
  size: number; // px
  /** Index del premio elegido por server (-1 si no se está spineando). */
  targetIndex: number;
  spinning: boolean;
  /** Llamado cuando termina la animación. */
  onSpinEnd?: () => void;
  /** Parámetros físicos desde settings de la sucursal. */
  physics?: PhysicsParams;
  /** Callback cada vez que pasa un segmento (para animar Flapper).
   *  intensity: 0..1 según velocidad actual normalizada. */
  onTick?: (intensity: number) => void;
  /** Color del anillo decorativo (migration 015). Default dorado. */
  colorBorde?: string;
  /** Grosor de las divisorias entre segmentos del wheel (migration
   *  017). Color = colorBorde. Default 3px. */
  grosorDivisoria?: number;
  /** Alineación del texto en cada segmento (migration 019). 'left' =
   *  el texto arranca cerca del centro y crece hacia afuera (default).
   *  'right' = el texto termina cerca del borde y crece hacia el centro.
   *  'center' = centrado en el espacio disponible. */
  textAlign?: "left" | "center" | "right";
  /** Estilo de sonido elegido por la sucursal (migration 029 + refactor
   *  2026-06-02). Lo usamos para dispatching de los eventos tick + blink. */
  soundStyle?: SoundStyle;
  /** Migration 040: cap del devicePixelRatio del canvas (nitidez vs
   *  rendimiento). Default 2 = el cap histórico de la ruleta. */
  dprMax?: number;
};

const TAU = Math.PI * 2;
const UMBRAL_STOP = 0.005; // velocidad bajo la cual paramos
const MIN_VUELTAS = 4; // mínimo de vueltas completas antes del target

// Desatura un color hex bajando su saturación al `factor` indicado
// (0..1). Pasamos a HSL, multiplicamos S por el factor, volvemos a
// RGB. Usado para que las divisorias del wheel (que reusan el color
// del anillo) tengan menos saturación = se vean más sobrias y no
// compitan visualmente con los segmentos coloridos. Pedido del user:
// "50% al menos de menor saturación, menos color más gris".
function desaturate(hex: string, factor: number): string {
  const m = hex.match(/^#?([0-9a-f]{6})$/i);
  if (!m) return hex;
  const num = parseInt(m[1], 16);
  const rN = ((num >> 16) & 0xff) / 255;
  const gN = ((num >> 8) & 0xff) / 255;
  const bN = (num & 0xff) / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rN) h = (gN - bN) / d + (gN < bN ? 6 : 0);
    else if (max === gN) h = (bN - rN) / d + 2;
    else h = (rN - gN) / d + 4;
    h /= 6;
  }
  const newS = s * factor;
  if (newS === 0) {
    const gray = Math.round(l * 255);
    const hexGray = gray.toString(16).padStart(2, "0");
    return `#${hexGray}${hexGray}${hexGray}`;
  }
  const q = l < 0.5 ? l * (1 + newS) : l + newS - l * newS;
  const p = 2 * l - q;
  function hue2rgb(t: number): number {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }
  const rOut = Math.round(hue2rgb(h + 1 / 3) * 255);
  const gOut = Math.round(hue2rgb(h) * 255);
  const bOut = Math.round(hue2rgb(h - 1 / 3) * 255);
  return `#${rOut.toString(16).padStart(2, "0")}${gOut.toString(16).padStart(2, "0")}${bOut.toString(16).padStart(2, "0")}`;
}

const DIVISORIA_SATURATION = 0.5; // 50% de la saturación original

// Estilo del "rim" claro del marco de la ruleta — usado en los
// trazados externos+internos del SVG del anillo. Se exporta para que
// otros componentes (SpinButton, Flapper) puedan usar EL MISMO color.
// Casi blanco con tinte cálido para máximo contraste sobre cualquier
// color del anillo (pedido del user: "bien clarito"). Independiente
// del colorBorde — igual que las luces parpadeantes.
// Grosor en ENTERO (3) porque CSS border con fractional pixels (2.5)
// se redondea inconsistentemente entre browsers y se ve más fino que
// el SVG stroke (que sí renderea fractional). El user reportó esa
// desincronía al matchear el borde del SpinButton con el rim del marco.
export const RIM_COLOR = "rgba(255,245,210,0.95)";
export const RIM_WIDTH = 3;

const DEFAULT_PHYSICS: PhysicsParams = {
  velocidadMin: 0.4,
  variacion: 0.6,
  frenado: 0.985,
  umbralCasiPasa: 0.2,
};

export function Wheel({
  premios,
  size,
  targetIndex,
  spinning,
  onSpinEnd,
  physics = DEFAULT_PHYSICS,
  onTick,
  colorBorde = "#c98b1c",
  grosorDivisoria = 3,
  textAlign = "left",
  soundStyle = "classic",
  dprMax = 2,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rotationRef = useRef(0); // radianes
  const animFrameRef = useRef<number | null>(null);
  const lastSegmentRef = useRef(0);
  const blinkTimerRef = useRef<number | null>(null);

  // CRÍTICO: premios viene del padre y cambia de referencia en cada
  // refetch de useAppState (polling cada 30s). Si lo metemos como dep
  // del useEffect de animación, ese refetch invalida el effect →
  // cancela animFrame y arranca OTRO giro con nuevo v0 random → el
  // cliente ve la ruleta "girar de la nada" mid-giro. Solución: ref
  // siempre con el último premios, sin incluir en deps del effect.
  const premiosRef = useRef(premios);
  premiosRef.current = premios;

  // Detecta la transición spinning false→true para arrancar UN solo
  // spin por click del cliente. Sin esto, si el useEffect re-corría
  // (cualquier dep cambia: size por resize, etc.), arrancaba otra
  // animación encima de la actual → ruleta "giraba dos veces" mid-spin.
  // Pattern testeado en ScratchGrid (allRevealedRef).
  const wasSpinningRef = useRef(false);

  // Refs para que el effect de animación pueda leer targetIndex y
  // onSpinEnd actualizados SIN re-runs. Si fueran deps del useEffect,
  // cualquier cambio mata el animFrame y dispara el bug del doble giro.
  // El loop chequea targetIndexRef.current en cada frame del freespin
  // para detectar la transición a un valor válido (server respondió)
  // y switchear a la fase de frenado.
  const targetIndexRef = useRef(targetIndex);
  targetIndexRef.current = targetIndex;
  const onSpinEndRef = useRef(onSpinEnd);
  onSpinEndRef.current = onSpinEnd;

  const n = premios.length;
  const seg = n > 0 ? TAU / n : 0;

  // divisoria local: empaqueta color + grosor para pasarlo a draw().
  // Color = colorBorde desaturado al 50% — más gris/sobrio para no
  // competir con los colores de cada segmento (pedido del user).
  const divisoria = {
    color: desaturate(colorBorde, DIVISORIA_SATURATION),
    grosor: grosorDivisoria,
  };
  // Opciones de texto que pasamos a draw() en cada redibuju.
  const textOpts: TextOpts = { align: textAlign };

  // Dibuja la rueda con la rotación actual.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || n === 0) return;
    // Clamp del DPR: en kioskos Android/retina el dpr puede ser 2-3 y el
    // backbuffer se rasteriza a 2-3x el tamaño CSS, encareciendo cada frame
    // (clearRect + N fills + N textos). Migration 040: el cap es configurable
    // (dprMax, default 2) — el dueño sube nitidez en pantallas densas a costa de
    // fillrate, o lo baja si la rueda va lenta.
    const dpr = Math.min(window.devicePixelRatio || 1, dprMax);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    // PERF (procesadores lentos): dibujamos la rueda UNA sola vez en rotación 0.
    // El giro NO redibuja el canvas (que es lo caro: N fills + N measureText por
    // frame) — se hace rotando el elemento <canvas> con transform:rotate() (GPU,
    // ver el loop de animación). El costo del giro pasa a ser independiente del
    // tamaño de la rueda -> se puede agrandar sin perder fluidez. Acá solo
    // re-rasterizamos el contenido (premios/colores/tamaño) y dejamos el canvas
    // en la rotación de reposo actual via CSS.
    draw(ctx, premios, size, 0, -1, divisoria, textOpts);
    canvas.style.transform = `rotate(${rotationRef.current}rad)`;
    // colorBorde/grosorDivisoria/textAlign SÍ van en deps: cambiarlos en el
    // admin debe RE-DIBUJAR el canvas estático (sino el preview no refleja el
    // cambio en vivo — bug reportado). Estos NO cambian durante un giro (son
    // settings del admin, estáticos en gameplay), así que re-correr este effect
    // por ellos solo pasa en el preview/admin, no interfiere con la animación.
    // dprMax igual (nitidez). divisoria/textOpts son objetos nuevos por render;
    // usamos las primitivas en deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premios, size, n, dprMax, colorBorde, grosorDivisoria, textAlign]);

  // Animación. Arranca cuando spinning pasa a true — incluso si
  // targetIndex aún es -1 (el padre todavía espera respuesta del
  // server). En ese caso, la rueda gira libre a velocidad constante
  // ("free spin"). Cuando targetIndex cambia a un valor válido (via
  // targetIndexRef), el loop detecta el cambio y transiciona a la
  // fase de frenado cubic ease-out con continuidad de velocidad —
  // el user nunca ve loading: la rueda ya estaba girando.
  //
  // Server-authoritative (§8): el premio lo sigue eligiendo el server.
  // Solo cambia *cuándo* el cliente recibe la info — durante el
  // freespin la rotación no apunta a nada decidido todavía. La
  // continuidad la garantiza que la velocidad inicial del cubic
  // matchea la del free spin (DURATION se calcula para eso).
  useEffect(() => {
    if (!spinning || n === 0) {
      wasSpinningRef.current = false;
      return;
    }
    if (wasSpinningRef.current) return;
    wasSpinningRef.current = true;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { velocidadMin, variacion, frenado } = physics;
    const FRAME_MS = 1000 / 60;

    // Velocidad del free spin (rad/frame). Algo arriba del promedio
    // del rango aleatorio histórico — se siente firme y "vivo". Se
    // usa además como velocidad inicial del cubic ease-out al
    // transicionar (DURATION se ajusta para que matchee y haya
    // continuidad).
    const freeSpinVel = velocidadMin + variacion * 0.6;

    // Tiempo MÍNIMO girando libre antes de poder frenar — evita que
    // si el server responde en 30ms, se vea "arranca y frena" abrupto.
    // Si la respuesta tarda más, igual seguimos girando hasta que llegue.
    const MIN_FREESPIN_MS = 600;

    let rotation = rotationRef.current;
    let phase: "freespin" | "decel" = "freespin";
    let lastFrameTime = performance.now();
    const startSpinTime = lastFrameTime;
    lastSegmentRef.current =
      Math.floor((((-rotation - Math.PI / 2) % TAU) + TAU) / seg) % n;

    // Variables del plan de frenado — se llenan al transicionar.
    let decelStartTime = 0;
    let decelStartRot = 0;
    let decelDelta = 0;
    let decelDuration = 0;
    let decelTargetIdx = -1;

    // Calcula el plan de frenado al recibir el target. Garantiza
    // llegada EXACTA al gajo elegido + continuidad de velocidad con
    // la fase freespin (velocidad inicial cubic = freeSpinVel).
    function buildDecelPlan(targetIdx: number) {
      // v0Random igual que antes — define cuántos frames "típicamente"
      // tardaría parar con frenado exponencial. Lo usamos para fijar
      // un MIN_VUELTAS adicional aproximado por consistencia con el
      // feel antiguo, pero la duración real la elegimos para preservar
      // la velocidad inicial.
      const v0Random = velocidadMin + Math.random() * variacion;
      const framesParaParar = Math.ceil(
        Math.log(UMBRAL_STOP / v0Random) / Math.log(frenado),
      );
      const totalRotRandom =
        (v0Random * (1 - Math.pow(frenado, framesParaParar))) / (1 - frenado);

      // Offset random dentro del segmento (10%..90% — no cae en línea).
      const offsetInSegment = seg * (0.1 + Math.random() * 0.8);
      const targetPoint = targetIdx * seg + offsetInSegment;
      const targetMod =
        (((-Math.PI / 2 - targetPoint) % TAU) + TAU) % TAU;

      const startMod = ((rotation % TAU) + TAU) % TAU;
      let delta = targetMod - startMod;
      if (delta <= 0) delta += TAU;
      // MIN_VUELTAS adicionales para emoción — encima de lo que ya hizo
      // el freespin.
      delta += MIN_VUELTAS * TAU;
      const delta2 =
        delta + Math.ceil(Math.max(0, totalRotRandom - delta) / TAU) * TAU;

      // Continuidad de velocidad: cubic ease-out en t=0 tiene derivada
      // 3·delta/DURATION. Queremos eso = freeSpinVel/FRAME_MS (vel en
      // rad/ms). Despejo:  DURATION = 3·delta·FRAME_MS / freeSpinVel
      const duration = (3 * delta2 * FRAME_MS) / freeSpinVel;

      decelStartTime = performance.now();
      decelStartRot = rotation;
      decelDelta = delta2;
      decelDuration = duration;
      decelTargetIdx = targetIdx;
      phase = "decel";
    }

    function animate() {
      const now = performance.now();

      if (phase === "freespin") {
        // Avance lineal: rotación += velocidad * frames transcurridos.
        const elapsed = now - lastFrameTime;
        rotation += freeSpinVel * (elapsed / FRAME_MS);
        rotationRef.current = rotation;
        if (canvas) canvas.style.transform = `rotate(${rotation}rad)`;

        // Sonido al pasar segmento — intensidad alta porque la velocidad
        // del freespin es constante y firme.
        const currentSegment =
          Math.floor((((-rotation - Math.PI / 2) % TAU) + TAU) / seg) % n;
        if (currentSegment !== lastSegmentRef.current) {
          playEvent("ruleta", "tick", soundStyle, { intensity: 0.85 });
          onTick?.(0.85);
          lastSegmentRef.current = currentSegment;
        }

        // ¿Llegó target del server Y pasó el mínimo de freespin?
        const targetReady = targetIndexRef.current >= 0;
        const minTimeOk = now - startSpinTime >= MIN_FREESPIN_MS;
        if (targetReady && minTimeOk) {
          buildDecelPlan(targetIndexRef.current);
        }

        lastFrameTime = now;
        animFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      // ── phase === 'decel' — cubic ease-out hasta target ──
      const elapsed = now - decelStartTime;
      const tNorm = Math.min(1, elapsed / decelDuration);
      const progress = tNorm >= 1 ? 1 : 1 - Math.pow(1 - tNorm, 3);
      rotation = decelStartRot + decelDelta * progress;
      rotationRef.current = rotation;
      if (canvas) canvas.style.transform = `rotate(${rotation}rad)`;

      const currentSegment =
        Math.floor((((-rotation - Math.PI / 2) % TAU) + TAU) / seg) % n;
      if (currentSegment !== lastSegmentRef.current) {
        const velNorm = Math.pow(1 - tNorm, 2);
        const intensity = Math.min(0.3 + velNorm * 0.7, 1);
        playEvent("ruleta", "tick", soundStyle, { intensity });
        onTick?.(intensity);
        lastSegmentRef.current = currentSegment;
      }

      if (tNorm < 1) {
        animFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Snap exacto al target.
        rotation = decelStartRot + decelDelta;
        rotationRef.current = rotation;
        if (canvas) canvas.style.transform = `rotate(${rotation}rad)`;
        animFrameRef.current = null;
        window.setTimeout(() => {
          if (ctx) {
            startBlinking(
              ctx,
              premiosRef.current,
              size,
              decelTargetIdx,
              () => onSpinEndRef.current?.(),
            );
          }
        }, 150);
      }
    }
    animFrameRef.current = requestAnimationFrame(animate);

    function startBlinking(
      ctx: CanvasRenderingContext2D,
      premios: PremioPublic[],
      size: number,
      targetIdx: number,
      done?: () => void,
    ) {
      const BLINK_COUNT = 3;
      const ON_MS = 180;
      const OFF_MS = 130;
      let i = 0;
      let on = false;
      function tick() {
        // Dibujamos en rotación 0: el canvas ya quedó rotado al gajo final por
        // CSS (transform en el snap). El gajo ganador (en su ángulo base)
        // aparece arriba via ese transform; acá solo prendemos/apagamos su
        // highlight. Mismas pocas redibujadas que antes (6), pero a rotación 0.
        if (i >= BLINK_COUNT * 2) {
          draw(ctx, premios, size, 0, -1, divisoria, textOpts);
          blinkTimerRef.current = null;
          done?.();
          return;
        }
        on = !on;
        draw(ctx, premios, size, 0, on ? targetIdx : -1, divisoria, textOpts);
        if (on) playEvent("ruleta", "blink", soundStyle);
        i++;
        blinkTimerRef.current = window.setTimeout(tick, on ? ON_MS : OFF_MS);
      }
      tick();
    }

    // NO cleanup acá (mismo motivo que antes — wasSpinningRef bloquea
    // re-arranques). El unmount real vive en el useEffect siguiente.
    // 'premios', 'onTick', 'onSpinEnd' y 'targetIndex' NO van en deps —
    // se acceden via refs para no re-disparar el effect mid-spin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinning, n, seg, size, physics]);

  // Cleanup al unmount real del componente. SEPARADO del effect del
  // trigger para que solo corra cuando el cliente sale de la ruleta
  // (navigate a otra ruta), no en cada cambio de dep del trigger.
  useEffect(() => {
    return () => {
      if (animFrameRef.current !== null) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      if (blinkTimerRef.current !== null) {
        window.clearTimeout(blinkTimerRef.current);
        blinkTimerRef.current = null;
      }
    };
  }, []);

  // El canvas (ruleta de premios) ocupa la mayor parte. El anillo +
  // luces van AFUERA del canvas, en la franja que queda entre el
  // canvas y el borde del contenedor. INNER_RATIO 0.94 = anillo de
  // 3% por lado (más fino que antes, pedido del user).
  const INNER_RATIO = 0.94;
  const innerSize = size * INNER_RATIO;
  const innerOffset = (size - innerSize) / 2;

  // Anillo "marquesina de feria" — implementado con SVG. CSS
  // radial-gradient con stops sharp + rounded-full producía aliasing
  // ("dientes de sierra") en el borde curvo; SVG <circle> tiene AA
  // nativo en el stroke.
  //
  // Estructura (en orden de render, último queda arriba):
  //   1. Fill grueso del colorBorde entre R*0.94 y R.
  //   2. Trazado externo "metálico": doble línea adyacente — una
  //      sombra fina contra el fill y un rim claro en el filo más
  //      externo. Da el efecto de bisel/bordes finos como en la
  //      referencia (referencia del user: borde con doble trazo).
  //   3. Sombra interna del marco: un <circle> lleno con
  //      radial-gradient que va de transparent (centro del wheel) a
  //      oscuro (borde del canvas), simulando profundidad/relieve.
  //      Reemplaza la línea negra interna que tenía antes —
  //      pedido explícito del user para imitar la referencia.
  //
  // zIndex={2}: el SVG va ENCIMA del canvas (z=1) para que la sombra
  // interna se vea sobre los segmentos del wheel. Las luces siguen
  // arriba de todo (z=3 dentro de WheelLights).
  const R = size / 2;
  const fillCenter = R * 0.97;
  const fillThickness = R * 0.06;

  return (
    <div
      className="relative"
      style={{ width: size, height: size }}
    >
      <svg
        className="pointer-events-none absolute inset-0"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{
          filter: "drop-shadow(0 0 30px rgba(0,0,0,0.3))",
          zIndex: 2,
        }}
      >
        <defs>
          {/* Sombra interna sutil sobre los segmentos del wheel.
              Refuerza la profundidad pero ya no reemplaza ningún
              trazado (ahora hay rim claro en el borde interno). */}
          <radialGradient
            id="ring-inner-shadow"
            cx="50%"
            cy="50%"
            r="50%"
          >
            <stop offset="85%" stopColor="rgba(0,0,0,0)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.35)" />
          </radialGradient>
        </defs>
        {/* Fill grueso del color configurado */}
        <circle
          cx={R}
          cy={R}
          r={fillCenter}
          stroke={colorBorde}
          strokeWidth={fillThickness}
          fill="none"
        />
        {/* Rim metálico DOBLE (externo + interno). Color dorado
            brillante FIJO, independiente del colorBorde — como las
            luces amarillas de marquesina clásica. El user pidió
            'lineas amarillas en ambos bordes' como en la referencia.
            Grosor 2.5px para que sean claramente visibles. Sombra
            fina adyacente al rim por dentro = efecto de bisel
            metálico, también en ambos lados. */}
        {/* — Lado externo del marco — */}
        <circle
          cx={R}
          cy={R}
          r={R - 3}
          stroke="rgba(0,0,0,0.4)"
          strokeWidth={1}
          fill="none"
        />
        <circle
          cx={R}
          cy={R}
          r={R - 1.25}
          stroke={RIM_COLOR}
          strokeWidth={RIM_WIDTH}
          fill="none"
        />
        {/* — Lado interno del marco — */}
        <circle
          cx={R}
          cy={R}
          r={R * 0.94 + 3}
          stroke="rgba(0,0,0,0.4)"
          strokeWidth={1}
          fill="none"
        />
        <circle
          cx={R}
          cy={R}
          r={R * 0.94 + 1.25}
          stroke={RIM_COLOR}
          strokeWidth={RIM_WIDTH}
          fill="none"
        />
        {/* Sombra interna gradient (sobre el wheel). Más sutil ahora
            porque el rim claro interno ya define el contorno. */}
        <circle
          cx={R}
          cy={R}
          r={R * 0.94}
          fill="url(#ring-inner-shadow)"
        />
      </svg>
      {/* Canvas centrado, MÁS CHICO que el contenedor para dejar
          espacio al anillo + luces alrededor. */}
      <canvas
        ref={canvasRef}
        style={{
          width: innerSize,
          height: innerSize,
          display: "block",
          position: "absolute",
          left: innerOffset,
          top: innerOffset,
          zIndex: 1,
          // PERF: el giro se hace con transform:rotate() sobre este canvas
          // (en vez de redibujar). will-change + origen centrado -> la rotación
          // la compositea la GPU, fluida aun en procesadores lentos.
          transformOrigin: "center center",
          willChange: "transform",
        }}
        className="rounded-full"
      />
      {/* Luces parpadeantes — incrustadas en la franja del anillo
          (radio ~97% del total, en el centro del gradient).
          OJO: color fijo amarillo-LED tipo bombilla. NO toman el color
          del anillo — el label en el admin dice "Color del anillo
          (borde)" y no debería afectar a las luces (principio de
          mínima sorpresa: lo que dice el label es lo que cambia). */}
      <WheelLights size={size} />
    </div>
  );
}

const NUM_LIGHTS = 24;
const LIGHT_ANIM_DURATION_S = 2.4; // duración del loop completo de cada luz
// Color fijo de las luces — amarillo-LED cálido tipo bombilla de
// marquesina de fairground. Independiente del color del anillo.
const LIGHT_COLOR = "rgb(255, 230, 130)";
const LIGHT_GLOW = "rgba(255, 215, 0, 0.9)";

function WheelLights({ size }: { size: number }) {
  // Radio = 97% del total. Centro del anillo delgado (94%-100%).
  const lightSize = Math.max(6, size * 0.022);
  const radio = (size / 2) * 0.97;
  const cx = size / 2;
  const cy = size / 2;
  return (
    <div
      className="pointer-events-none absolute inset-0"
      style={
        {
          zIndex: 3,
          // CSS var consumida por @keyframes wheel-light-pulse para el glow.
          "--wheel-light-color": LIGHT_GLOW,
        } as React.CSSProperties
      }
    >
      {Array.from({ length: NUM_LIGHTS }).map((_, i) => {
        const angle = (i / NUM_LIGHTS) * Math.PI * 2 - Math.PI / 2;
        const x = cx + radio * Math.cos(angle) - lightSize / 2;
        const y = cy + radio * Math.sin(angle) - lightSize / 2;
        // Cada luz tiene un delay distinto → efecto "corriendo".
        const delay = (i / NUM_LIGHTS) * LIGHT_ANIM_DURATION_S;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x,
              top: y,
              width: lightSize,
              height: lightSize,
              borderRadius: "50%",
              background: LIGHT_COLOR,
              animation: `wheel-light-pulse ${LIGHT_ANIM_DURATION_S}s ease-in-out ${delay}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

// Helper de dibujo — pura.
// highlightIdx >= 0: dibuja overlay blanco brillante sobre ese
// segmento (usado para parpadear el ganador).
// divisoria: { color, grosor } controla las líneas entre segmentos.
// textOpts: configuración de cómo se posiciona/alinea el texto en
// cada segmento (migration 019).
type DivisoriaOpts = { color: string; grosor: number };
type TextOpts = { align: "left" | "center" | "right" };
const DEFAULT_DIVISORIA: DivisoriaOpts = {
  color: "rgba(255,255,255,0.15)",
  grosor: 2,
};
const DEFAULT_TEXT_OPTS: TextOpts = { align: "left" };
function draw(
  ctx: CanvasRenderingContext2D,
  premios: PremioPublic[],
  size: number,
  rotation: number,
  highlightIdx = -1,
  divisoria: DivisoriaOpts = DEFAULT_DIVISORIA,
  textOpts: TextOpts = DEFAULT_TEXT_OPTS,
) {
  const cx = size / 2;
  const cy = size / 2;
  // Antes era size/2 - 4 (4px de margen) + un ctx.stroke negro grueso
  // alrededor para "cerrar" el wheel. Combinado generaba una franja
  // oscura visible entre los segmentos coloridos y el borde interno
  // del marco SVG. Ahora el marco es un anillo claro con su propia
  // sombra interna gradient, así que el wheel debe llegar hasta el
  // filo del canvas sin margen. Restamos 0.5 para no recortar los
  // strokes blancos entre segmentos por el clip rounded-full.
  const r = size / 2 - 0.5;
  const n = premios.length;
  const seg = TAU / n;

  ctx.clearRect(0, 0, size, size);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotation);

  for (let i = 0; i < n; i++) {
    const startAng = i * seg;
    const endAng = startAng + seg;
    const color = premios[i].color ?? defaultColor(i);

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, r, startAng, endAng);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    // Divisoria entre segmentos. Reutiliza el color del anillo
    // (configurable) y el grosor desde admin → todo el "marco" del
    // wheel tiene la misma identidad visual.
    ctx.lineWidth = divisoria.grosor;
    ctx.strokeStyle = divisoria.color;
    ctx.stroke();

    ctx.save();
    ctx.rotate(startAng + seg / 2);
    // textAlign desde admin (migration 019). Cada valor cambia el
    // punto de anclaje del texto:
    //   - 'left':   ancla en x=PADDING_IN (cerca del centro), crece
    //               hacia el borde externo. Todos los textos arrancan
    //               en el mismo punto cerca del centro = aspecto de
    //               'rayos' (default y recomendado por estética).
    //   - 'right':  ancla en x=r-PADDING_OUT (borde externo), crece
    //               hacia el centro. Era el default histórico.
    //   - 'center': ancla en el punto medio del rango disponible,
    //               texto centrado en el segmento radial.
    ctx.textAlign = textOpts.align;
    ctx.textBaseline = "middle";
    ctx.fillStyle = textColor(color);
    // Nombres de premios en el wheel = slot 'display' (texto promocional).
    // getCanvasFont lee la CSS var --font-display al momento de dibujar,
    // así si el admin cambia la fuente en runtime se aplica al próximo
    // redraw (próximo giro o resize).
    //
    // Estrategia de fitting (pedido del user: priorizar visibilidad,
    // nada de truncar con '…'):
    //   1. Tamaño base size*0.028 (antes 0.038 — se veía muy grande
    //      incluso para wheels grandes).
    //   2. Si la única palabra no entra → shrink al ratio que falta.
    //   3. Si hay espacios y el texto entero no entra → partir en 2
    //      líneas balanceadas, posicionarlas arriba/abajo del eje.
    //   4. Si una línea aún no entra → shrink combinado.
    //   maxWidth = espacio disponible desde 'r - PADDING' hacia el
    //   centro del wheel. Restamos PADDING (14) para que el texto no
    //   tope el borde exterior.
    const text = premios[i].nombre;
    const PADDING_OUT = 14; // pixels de margen contra el borde externo
    const PADDING_IN = 70;  // pixels reservados al centro (botón GIRAR)
    const maxWidth = r - PADDING_OUT - PADDING_IN;
    const baseFontSize = Math.max(11, size * 0.028);
    const minFontSize = 9; // límite duro, debajo no se lee

    // Función pura: dado un texto y un fontSize, devuelve el ancho.
    function widthAt(t: string, fs: number): number {
      ctx.font = getCanvasFont("display", 700, fs);
      return ctx.measureText(t).width;
    }

    // Función pura: dado un array de palabras, busca el punto de split
    // que mejor balancea las 2 mitades (diferencia mínima de longitud).
    function bestSplit(ws: string[]): [string, string] {
      let bestS = 1;
      let bestDiff = Infinity;
      for (let s = 1; s < ws.length; s++) {
        const l = ws.slice(0, s).join(" ");
        const r2 = ws.slice(s).join(" ");
        const d = Math.abs(l.length - r2.length);
        if (d < bestDiff) {
          bestDiff = d;
          bestS = s;
        }
      }
      return [ws.slice(0, bestS).join(" "), ws.slice(bestS).join(" ")];
    }

    const words = text.split(/\s+/).filter(Boolean);
    const fullWidth = widthAt(text, baseFontSize);

    // Punto de anclaje según alignment elegido (migration 019).
    // Como ctx.textAlign ya está seteado, fillText posiciona EL ANCLA
    // del texto en xAnchor. Cada modo usa un xAnchor distinto para
    // que el texto quede dentro del rango [PADDING_IN, r-PADDING_OUT]:
    //   - left:   xAnchor = PADDING_IN, ancla = izquierda del texto.
    //   - right:  xAnchor = r-PADDING_OUT, ancla = derecha del texto.
    //   - center: xAnchor = punto medio, ancla = centro del texto.
    const xAnchor =
      textOpts.align === "right"
        ? r - PADDING_OUT
        : textOpts.align === "center"
        ? (PADDING_IN + r - PADDING_OUT) / 2
        : PADDING_IN;

    if (fullWidth <= maxWidth || words.length < 2) {
      // Cabe en una línea, o no se puede partir (1 sola palabra).
      let fs = baseFontSize;
      if (fullWidth > maxWidth) {
        fs = Math.max(minFontSize, baseFontSize * (maxWidth / fullWidth));
      }
      ctx.font = getCanvasFont("display", 700, fs);
      ctx.fillText(text, xAnchor, 0);
    } else {
      // 2 líneas balanceadas.
      const [line1, line2] = bestSplit(words);
      const w1 = widthAt(line1, baseFontSize);
      const w2 = widthAt(line2, baseFontSize);
      const maxLine = Math.max(w1, w2);
      let fs = baseFontSize;
      if (maxLine > maxWidth) {
        fs = Math.max(minFontSize, baseFontSize * (maxWidth / maxLine));
      }
      ctx.font = getCanvasFont("display", 700, fs);
      // Separación vertical: ~0.55 del fontSize entre baseline de cada
      // línea respecto al eje radial del segmento.
      ctx.fillText(line1, xAnchor, -fs * 0.55);
      ctx.fillText(line2, xAnchor, fs * 0.55);
    }
    ctx.restore();
  }

  // Refuerzo de divisoria entre gajos del MISMO color. Si dos gajos
  // vecinos comparten color, la divisoria normal (tono del anillo
  // desaturado) casi no contrasta y los dos se leen como UN gajo
  // gigante (reporte del user). SOLO en esos bordes dibujamos una linea
  // de contraste — clara si el color es oscuro, oscura si es claro
  // (misma logica de luminancia que textColor) — para que siempre se
  // note la separacion. Los bordes de distinto color NO se tocan: la
  // divisoria sutil que pidio el user se mantiene tal cual.
  if (n > 1) {
    for (let i = 0; i < n; i++) {
      const colA = premios[i].color ?? defaultColor(i);
      const j = (i + 1) % n;
      const colB = premios[j].color ?? defaultColor(j);
      if (colA !== colB) continue;
      ctx.save();
      ctx.rotate((i + 1) * seg);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(r, 0);
      ctx.lineWidth = Math.max(divisoria.grosor, 2);
      ctx.strokeStyle =
        textColor(colA) === "#fff"
          ? "rgba(255,255,255,0.7)"
          : "rgba(0,0,0,0.45)";
      ctx.stroke();
      ctx.restore();
    }
  }

  // Overlay blanco brillante sobre el segmento highlighted (parpadeo
  // del ganador). Va encima de la fill normal y el texto.
  if (highlightIdx >= 0 && highlightIdx < n) {
    const startAng = highlightIdx * seg;
    const endAng = startAng + seg;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, r, startAng, endAng);
    ctx.closePath();
    ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
    ctx.fill();
    // Borde dorado brillante para destacar más
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(255, 215, 0, 0.9)";
    ctx.stroke();
  }

  // ANTES: ctx.stroke con rgba(0,0,0,0.4) y lineWidth 6 cerraba el wheel
  // con un borde negro grueso. Eso generaba una franja oscura visible
  // entre los segmentos y el borde interno del marco SVG, que el user
  // vio como "línea negra". El marco ya tiene su propia sombra interna
  // gradient (definida en el JSX del componente), no hace falta este
  // stroke acá.

  ctx.restore();
}

const DEFAULT_COLORS = [
  "#ff6b6b",
  "#ffd93d",
  "#6bcb77",
  "#4d96ff",
  "#c77dff",
  "#ff9a3c",
  "#00c9b1",
  "#ff6f91",
  "#845ec2",
  "#f9f871",
];

function defaultColor(i: number): string {
  return DEFAULT_COLORS[i % DEFAULT_COLORS.length];
}

function textColor(bgHex: string): string {
  const m = bgHex.match(/^#?([0-9a-f]{6})$/i);
  if (!m) return "#fff";
  const num = parseInt(m[1], 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#1a1540" : "#fff";
}
